import { memo, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  getActiveTab,
  getCurrentFleetId,
  getMissionPlannerPeriod,
  fetchGetMissionQueue,
  getSelectedVehicleIds,
  updateMissionsQueue,
  getSearchValue,
  getMissionRowsLimit,
  getIsMissionsUpdatesPaused,
  getMissionsUpdatesStorage,
  resetMissionsUpdatesStorage,
  setCurrentFleetId,
  getCountsRefreshToken,
} from "Slices/missionPlanner";
import {
  DEPLOYED_MISSION_LIST_MODAL,
  toggleModal,
  getIsModalOpen,
} from "Slices/modals";
import { MISSION_PANNER_WS, WS_SPINNER_LOGS } from "Utils/constants";
import { useFMSWebsocket } from "Utils/webSocketUtils";
import { WebsocketLoaderContext } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { FMSLogger } from "FMSLogger";
import {
  isValidIdentifier,
  sanitizeSearchParams,
} from "Utils/navigationValidation";

const wsSpinnerLogs = FMSLogger.byPrefix(`${WS_SPINNER_LOGS}:MissionPlanner`);

export const MissionPlannerController = memo(() => {
  const location = useLocation();
  const currentFleetId = useSelector(getCurrentFleetId);
  const selectedVehicleIds = useSelector(getSelectedVehicleIds);
  const period = useSelector(getMissionPlannerPeriod);
  const limit = useSelector(getMissionRowsLimit);
  const searchValue = useSelector(getSearchValue);
  const activeTab = useSelector(getActiveTab);
  const isMissionsUpdatesPaused = useSelector(getIsMissionsUpdatesPaused);
  const missionsUpdatesStorage = useSelector(getMissionsUpdatesStorage);
  const isAnyModalOpen = useSelector(getIsModalOpen);
  const countsRefreshToken = useSelector(getCountsRefreshToken);
  const dispatch = useDispatch();

  const countsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The counts refresh is debounced 800ms and triggered only by the token, so
  // its effect closure can go stale if the user changes a filter while a timer
  // is pending. The count aggregates are scoped by period/search (not status),
  // so read the LATEST filters from this ref at fire time instead of from the
  // token effect's stale closure — and without re-arming the timer on every
  // filter change (the filter effect already refetches counts on those).
  const countsFiltersRef = useRef({
    currentFleetId,
    activeTab,
    selectedVehicleIds,
    period,
    limit,
    searchValue,
  });
  countsFiltersRef.current = {
    currentFleetId,
    activeTab,
    selectedVehicleIds,
    period,
    limit,
    searchValue,
  };

  const setIsWSConnecting = useContext(WebsocketLoaderContext);

  // Open modal when navigating to /missions/list
  useEffect(() => {
    if (location.pathname === "/missions/list" && !isAnyModalOpen) {
      // Check if fleet ID is in URL params
      const searchParams = new URLSearchParams(
        sanitizeSearchParams(location.search)
      );
      const fleetIdFromUrl = searchParams.get("fleetId");

      // If fleet ID is in URL but not in store, set it (validate UUID format)
      if (
        fleetIdFromUrl &&
        isValidIdentifier(fleetIdFromUrl) &&
        !currentFleetId
      ) {
        dispatch(setCurrentFleetId(fleetIdFromUrl));
      }

      // Use fleet ID from URL if available and valid, otherwise use from store
      const fleetIdToUse =
        fleetIdFromUrl && isValidIdentifier(fleetIdFromUrl)
          ? fleetIdFromUrl
          : currentFleetId;
      dispatch(
        toggleModal({
          type: DEPLOYED_MISSION_LIST_MODAL,
          data: { fleetId: fleetIdToUse },
        })
      );
    }
  }, [location.pathname, isAnyModalOpen, currentFleetId, dispatch]);

  useLayoutEffect(
    () => () => {
      wsSpinnerLogs.debug("unmount component spinner off");
      setIsWSConnecting?.(false);
    },
    []
  );

  useEffect(() => {
    if (currentFleetId) {
      wsSpinnerLogs.debug("new fleet id spinner on");
      setIsWSConnecting?.(true);
    } else {
      wsSpinnerLogs.debug("no fleet id spinner off");
      setIsWSConnecting?.(false);
    }
  }, [currentFleetId]);

  useFMSWebsocket({
    url: "/fleets/mission_queue/",
    subscriptionData: currentFleetId,
    loggerFlag: MISSION_PANNER_WS,
    getDataForSubscribe: (subscriptionId) => ({
      fleet_ids: [subscriptionId],
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      fleet_ids: [subscriptionId],
    }),
    handleInitialMessage: () => {
      wsSpinnerLogs.debug("create message spinner off");
      setIsWSConnecting?.(false);
      // Refresh on every WS connect/reconnect so missions that transitioned
      // while the socket was down (e.g. CANCELED) are not left stale.
      if (currentFleetId && period) {
        dispatch(
          fetchGetMissionQueue({
            currentFleetId,
            activeTab,
            selectedVehicleIds,
            period,
            limit,
            searchValue,
          })
        );
      }
    },
    handleMessage: (data) => {
      const shouldIgnoreUpdate = data?.mission?.state === "STATE_RUNNING";
      if (shouldIgnoreUpdate) return;

      // `typeof null === "object"` in JS — guard against null so a removed
      // mission doesn't silently no-op and leave stale CANCEL_PENDING state.
      if (data?.missions != null && typeof data?.missions === "object") {
        dispatch(updateMissionsQueue(data.missions));
      } else {
        dispatch(updateMissionsQueue(data));
      }
    },
  });

  // get mission queue if filters has been changed
  useEffect(() => {
    if (!currentFleetId || !period) return;
    dispatch(
      fetchGetMissionQueue({
        currentFleetId,
        activeTab,
        selectedVehicleIds,
        period,
        limit,
        searchValue,
      })
    );
  }, [
    currentFleetId,
    activeTab,
    selectedVehicleIds.length,
    period,
    limit,
    searchValue,
  ]);

  // update missions from storage
  useEffect(() => {
    if (
      isMissionsUpdatesPaused !== false ||
      missionsUpdatesStorage.length === 0
    )
      return;

    missionsUpdatesStorage.forEach((update) => {
      dispatch(updateMissionsQueue(update));
    });
    dispatch(resetMissionsUpdatesStorage());
  }, [isMissionsUpdatesPaused, missionsUpdatesStorage]);

  // Live tab badges: when a WS frame changed the in-memory list's membership
  // (reducer bumped countsRefreshToken), refresh the All/Deployed/Completed
  // counts from the server — the only source of the fleet-wide aggregates.
  // Debounced so a burst of frames coalesces into one lightweight refetch, and
  // skipped on the initial mount (token 0) since the filter effect already
  // fetched. Well under the 3s propagation bar.
  useEffect(() => {
    if (!countsRefreshToken || !currentFleetId || !period) return;
    if (countsTimerRef.current) clearTimeout(countsTimerRef.current);
    countsTimerRef.current = setTimeout(() => {
      const filters = countsFiltersRef.current;
      // Re-read & re-narrow at fire time: the user may have cleared the fleet/
      // period during the debounce window. The thunk requires both non-null.
      if (!filters.currentFleetId || !filters.period) return;
      dispatch(
        fetchGetMissionQueue({
          ...filters,
          // Re-apply the post-guard narrowing: the spread widens these back to
          // nullable, but the thunk requires non-null. Not redundant — required
          // for the type to hold; the guard above is the real runtime check.
          currentFleetId: filters.currentFleetId,
          period: filters.period,
          countersOnly: true,
        })
      );
    }, 800);
    return () => {
      if (countsTimerRef.current) clearTimeout(countsTimerRef.current);
    };
  }, [countsRefreshToken]);

  return null;
});
